import { useState, useRef, useCallback } from 'react';
import { designAHU } from '../helpers/bot/designAHU';
import {
  BotSetValue,
  DesignAHUResult,
  IAccessDoorParams,
  IAHUParams,
  IBotData,
  ICoilParams,
  IErWheelParams,
  IIdNameValue,
} from '../models';
import { BOT_FORM_STORAGE_KEY } from '../helpers/data/bot';
import { generateTimestampedName } from '../helpers/index';
import { track } from '../../../app/analytics';

interface UseAhuDesignParams {
  botData: IBotData;
  ahuParams: IAHUParams;
  rotaryParams: IErWheelParams;
  coilParams: ICoilParams;
  accessDoorParams: IAccessDoorParams;
  connHands: IIdNameValue[];
  deleteAHU: (ahuId: number) => Promise<void>;
  setValue: BotSetValue;
}

export const useAhuDesign = ({
  botData,
  ahuParams,
  rotaryParams,
  coilParams,
  accessDoorParams,
  connHands,
  deleteAHU,
  setValue,
}: UseAhuDesignParams) => {
  const [isDesigning, setIsDesigning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const createdAhuIdRef = useRef<number | null>(null);

  const resetUI = useCallback((error = false) => {
    setIsDesigning(false);
    setProgress(0);
    if (!error) setError(null);
    setSuccess(null);
    createdAhuIdRef.current = null;
  }, []);

  const cancelDesign = useCallback(async () => {
    setIsDesigning(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const ahuIdToDelete = createdAhuIdRef.current;
    if (ahuIdToDelete) {
      try {
        await deleteAHU(ahuIdToDelete);
      } catch (deleteError) {
        console.error('Failed to delete AHU on cancel:', deleteError);
      }
      createdAhuIdRef.current = null;
    }

    resetUI();
  }, [deleteAHU, resetUI]);

  const startDesign = useCallback(async () => {
    if (!ahuParams) return;
    // ✅ create fresh cleaned copy
    const cleanedBotData: IBotData = {
      ...botData,
      tunnels: botData.tunnels.map((tunnel) => ({
        ...tunnel,
        sections: tunnel.sections.map((section) => ({
          ...section,
          error: undefined,
        })),
      })),
    };

    // ✅ update state
    setValue(cleanedBotData);
    setIsDesigning(true);
    setProgress(0);
    setError(null);
    setSuccess(null);
    createdAhuIdRef.current = null;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // One kai_select_design_run per run, whichever branch ends it: the success
    // path can still throw into the catch below (e.g. reading the saved form),
    // and that must not add a second, failed, run.
    let runReported = false;
    const reportRun = (success: boolean) => {
      if (runReported) return;
      runReported = true;
      track('kai_select_design_run', { success });
    };

    try {
      const result: DesignAHUResult = await designAHU({
        botData: cleanedBotData,
        ahuParams,
        rotaryParams,
        coilParams,
        accessDoorParams,
        connHands,
        projectId: botData.project_id as number,
        onProgress: setProgress,
        abortSignal: abortController.signal,
        onAhuCreated: (ahuId) => {
          createdAhuIdRef.current = ahuId;
        },
      });

      if (abortController.signal.aborted) return;

      if (result.success && result.ahuId) {
        // Counted here, not in step1CreateAHU: a design that fails after
        // step 1 deletes the AHU it created (below), so only a completed
        // design leaves an AHU behind. One design = one AHU = one event.
        track('ahu_created');
        reportRun(true);
        resetUI();
        setSuccess('You have created your Air Handling Unit successfully!');
        localStorage.setItem('bot', 'true');
        localStorage.setItem(`generate_model_${result.ahuId}`, 'true');
        window.open(`/ahu/detail/${result.ahuId}`, '_blank');
        const raw =
          typeof window !== 'undefined'
            ? window.localStorage.getItem(BOT_FORM_STORAGE_KEY)
            : null;
        const parsed = JSON.parse(raw || '') as IBotData;
        if (!parsed?.tag) {
          setValue((draft) => {
            draft.tag = generateTimestampedName();
          });
        }
      } else {
        reportRun(false);
        // Handle section errors if present
        if (result.sectionErrors && result.sectionErrors.length > 0) {
          setValue((draft: IBotData) => {
            const errorsBySectionId = new Map(
              result.sectionErrors!.map((e) => [e.sectionId, e.error] as const),
            );
            for (const tunnel of draft.tunnels) {
              for (let i = 0; i < tunnel.sections.length; i += 1) {
                const section = tunnel.sections[i];
                const nextError = errorsBySectionId.get(section.id);
                if (nextError != null) {
                  tunnel.sections[i] = { ...section, error: nextError };
                } else if (section.error) {
                  tunnel.sections[i] = { ...section, error: undefined };
                }
              }
            }
          });
          setError('');
        } else {
          setError(result.error || 'Something went wrong');
        }

        const ahuIdToDelete = createdAhuIdRef.current || result.ahuId;
        if (ahuIdToDelete) {
          try {
            await deleteAHU(ahuIdToDelete);
          } catch (deleteError) {
            console.error('Failed to delete AHU:', deleteError);
          }
        }

        resetUI(true);
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || abortController.signal.aborted) {
        setError('Oops! I have a problem creating your Air Handling Unit.');
        const ahuIdToDelete = createdAhuIdRef.current;
        if (ahuIdToDelete) {
          try {
            await deleteAHU(ahuIdToDelete);
          } catch (deleteError) {
            console.error('Failed to delete AHU on abort:', deleteError);
          }
          createdAhuIdRef.current = null;
        }
        resetUI();
        return;
      }

      // A user cancel returned above; this run failed outright.
      reportRun(false);

      if (createdAhuIdRef.current) {
        try {
          await deleteAHU(createdAhuIdRef.current);
        } catch (deleteError) {
          console.error('Failed to delete AHU:', deleteError);
        }
      }

      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(
        errorMessage ||
          'Oops! I have a problem creating your Air Handling Unit.',
      );
      resetUI();
    } finally {
      abortControllerRef.current = null;
    }
  }, [
    botData,
    ahuParams,
    coilParams,
    connHands,
    deleteAHU,
    setValue,
    resetUI,
    botData.floor_construction_id,
  ]);

  return {
    isDesigning,
    progress,
    error,
    success,
    startDesign,
    cancelDesign,
    resetUI,
  };
};
